import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, Heart, Brain, HandHeart, GraduationCap, 
  Users, Sparkles, ArrowRight, Play, Target, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const moduleIcons = {
  intellectual: BookOpen,
  character: Heart,
  wellness: Brain,
  service: HandHeart
};

const moduleColors = {
  intellectual: "from-indigo-500 to-blue-600",
  character: "from-rose-500 to-pink-600",
  wellness: "from-emerald-500 to-teal-600",
  service: "from-amber-500 to-orange-600"
};

export default function FoundationHub() {
  const [overview, setOverview] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, coursesRes] = await Promise.all([
          fetch(`${API}/api/foundation/overview`),
          fetch(`${API}/api/foundation/courses`)
        ]);
        
        if (overviewRes.ok) setOverview(await overviewRes.json());
        if (coursesRes.ok) {
          const data = await coursesRes.json();
          setCourses(data.courses?.slice(0, 3) || []);
        }
      } catch (err) {
        console.error('Error fetching foundation data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-indigo-50/30" data-testid="foundation-hub">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="bg-white/20 text-white mb-4">F3: Foundation</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              School 4 Thought
            </h1>
            <p className="text-xl text-indigo-100 mb-6 max-w-2xl">
              The intellectual, ethical, and emotional operating system of your legacy.
              Build the foundation that makes everything else possible.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/foundation/school">
                <Button size="lg" className="bg-white text-indigo-900 hover:bg-indigo-50">
                  <GraduationCap className="mr-2 h-5 w-5" />
                  Start Learning
                </Button>
              </Link>
              <Link to="/foundation/character">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Heart className="mr-2 h-5 w-5" />
                  Daily Reflection
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 space-y-12">
        {/* Core Modules */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-stone-800 mb-2">Four Pillars of Growth</h2>
            <p className="text-stone-600">Mind. Character. Wellness. Service.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {overview?.modules?.map((module, idx) => {
              const Icon = moduleIcons[module.id] || BookOpen;
              const colorClass = moduleColors[module.id] || "from-gray-500 to-gray-600";
              const links = {
                intellectual: "/foundation/school",
                character: "/foundation/character",
                wellness: "/foundation/wellness",
                service: "/foundation/service"
              };
              
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={links[module.id]}>
                    <Card className="h-full hover:shadow-lg transition-all group cursor-pointer border-2 border-transparent hover:border-indigo-200">
                      <CardHeader>
                        <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClass} text-white w-fit mb-3 group-hover:scale-110 transition-transform`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <CardTitle className="text-lg">{module.name}</CardTitle>
                        <CardDescription>{module.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <span className="text-indigo-600 text-sm font-medium flex items-center">
                          Explore <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Featured Courses */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-stone-800">Featured Courses</h2>
              <p className="text-stone-600">Start your intellectual journey</p>
            </div>
            <Link to="/foundation/school">
              <Button variant="outline">View All Courses</Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((course, idx) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={course.access_tier === "free" ? "secondary" : "default"}>
                        {course.access_tier === "free" ? "Free" : "Premium"}
                      </Badge>
                      <Badge variant="outline">{course.level}</Badge>
                    </div>
                    <CardTitle className="text-lg">{course.title}</CardTitle>
                    <CardDescription className="line-clamp-2">{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-stone-500 mb-4">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {course.duration_weeks} weeks
                      </span>
                      <span className="flex items-center">
                        <Target className="h-4 w-4 mr-1" />
                        {course.modules?.length || 0} modules
                      </span>
                    </div>
                    <Link to={`/foundation/school/${course.id}`}>
                      <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                        <Play className="mr-2 h-4 w-4" />
                        Start Course
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Stats & CTA */}
        <section className="grid md:grid-cols-2 gap-8">
          {/* Stats */}
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                Platform Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-indigo-600">{overview?.stats?.total_courses || 5}</div>
                  <p className="text-sm text-stone-500">Total Courses</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-emerald-600">{overview?.stats?.free_courses || 3}</div>
                  <p className="text-sm text-stone-500">Free Courses</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-amber-600">{overview?.stats?.total_badges || 10}</div>
                  <p className="text-sm text-stone-500">Badges to Earn</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-rose-600">{overview?.stats?.reflection_prompts || 30}</div>
                  <p className="text-sm text-stone-500">Daily Reflections</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mind Dashboard CTA */}
          <Card className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
            <CardContent className="py-8 flex flex-col justify-center h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Users className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Mind Dashboard</h3>
                  <p className="text-purple-100">Track your growth across all pillars</p>
                </div>
              </div>
              <p className="text-purple-100 mb-6">
                See your courses, reflections, service hours, and wellness journey—all in one place.
                Your story of growth, visualized.
              </p>
              <Link to="/foundation/dashboard">
                <Button className="bg-white text-indigo-700 hover:bg-indigo-50 w-full">
                  View Your Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Service Ledger CTA */}
        <section>
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="py-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-amber-100 rounded-xl">
                    <HandHeart className="h-10 w-10 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-800">Service Ledger</h3>
                    <p className="text-stone-600">Log volunteer hours, unlock benefits, build community</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center px-4">
                    <div className="text-2xl font-bold text-amber-600">5 hrs</div>
                    <p className="text-xs text-stone-500">Book Club Access</p>
                  </div>
                  <div className="text-center px-4 border-l">
                    <div className="text-2xl font-bold text-amber-600">20 hrs</div>
                    <p className="text-xs text-stone-500">Scholarship Eligible</p>
                  </div>
                  <div className="text-center px-4 border-l">
                    <div className="text-2xl font-bold text-amber-600">100 hrs</div>
                    <p className="text-xs text-stone-500">Ambassador</p>
                  </div>
                  <Link to="/foundation/service">
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      Log Hours
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
